import { InjectionToken } from '@angular/core';

/**
 * What a given rendering of an evaluation is allowed to hand to the user.
 *
 * Deliberately phrased as capabilities rather than as a "demo" flag: the
 * library should not know why something is withheld, only that it is. That
 * keeps the same switches usable for other restricted views — a free-plan
 * preview, say — without touching the components again.
 *
 * This is the *policy* of the rendering, the same whoever is looking. What
 * the current user may *change* on the answer in front of them is the other
 * axis, `AnswerPermissionsService`, and a button that both downloads and
 * depends on a section (the offerta-tecnica template, bulk compile) checks
 * both.
 *
 * These hide UI. They are not a security boundary: the endpoints themselves
 * must reject what the caller is not entitled to.
 */
export interface EvaluationCapabilities {
  /** Export the whole compiled evaluation. */
  canExport: boolean;
  /** Download a generated PEF report. Generating and viewing it is separate. */
  canDownloadPefReport: boolean;
  /** Download the blank offerta tecnica template. */
  canDownloadTemplate: boolean;
  /**
   * Run "compila tutte le risposte". Compiling a single question with its own
   * button is separate, and stays available.
   */
  canBulkCompile: boolean;
}

/** Everything allowed — what an ordinary evaluation gets. */
export const FULL_EVALUATION_CAPABILITIES: EvaluationCapabilities = {
  canExport: true,
  canDownloadPefReport: true,
  canDownloadTemplate: true,
  canBulkCompile: true,
};

/**
 * Injected by every download surface. Unprovided means unrestricted, so
 * existing routes need no change.
 */
export const EVALUATION_CAPABILITIES =
  new InjectionToken<EvaluationCapabilities>('EVALUATION_CAPABILITIES', {
    providedIn: 'root',
    factory: () => FULL_EVALUATION_CAPABILITIES,
  });
